import type { alerts } from '@cwos/api-client';
import type { AttentionItem, AttentionSeverity } from '@cwos/ui-admin';

/**
 * Notifications / attention pure logic (doc 04 §4). The header badge and the
 * panel both derive from the alerts domain — a single source of truth.
 *
 * Alert lifecycle (owner #5067, point 5):
 *   - open        → requires attention
 *   - acknowledged→ taken ownership (still unresolved)
 *   - escalated   → unresolved past configured escalation criteria
 *   - resolved    → completed
 * "Needs attention" == unresolved (open / acknowledged / assigned / escalated).
 * Escalation is a state transition, not an acknowledgement workflow — an alert
 * escalates directly from open when it passes its escalation criteria.
 */

export const ATTENTION_STATUSES = ['open', 'acknowledged', 'assigned', 'escalated'] as const;

const STATUS_SET = new Set<string>(ATTENTION_STATUSES);

export function isAttentionStatus(status: string): boolean {
  return STATUS_SET.has(status);
}

/**
 * Derive the header badge count from the alerts list `total` summary (counts by
 * status). Only unresolved statuses count.
 */
export function deriveAttentionCount(total: readonly { status: string; count: number }[]): number {
  return total.filter((t) => STATUS_SET.has(t.status)).reduce((sum, t) => sum + t.count, 0);
}

const SEV_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/** Unresolved alerts, severity-first then newest-first (stable, non-mutating). */
export function sortAttentionAlerts(list: readonly alerts.Alert[]): alerts.Alert[] {
  return [...list]
    .filter((a) => STATUS_SET.has(a.status))
    .sort((a, b) => {
      const sa = SEV_RANK[a.severity] ?? 3;
      const sb = SEV_RANK[b.severity] ?? 3;
      if (sa !== sb) return sa - sb;
      return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    });
}

/** Human relative age label ("just now", "12m ago", "3h ago", "2d ago"). */
export function ageLabel(createdAt: string, now: number = Date.now()): string {
  const then = Date.parse(createdAt);
  if (Number.isNaN(then)) return '';
  const mins = Math.floor(Math.max(0, now - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const ENTITY_LABELS: Record<string, string> = {
  session: 'Session',
  client: 'Client',
  worker: 'Worker',
  service: 'Service',
  service_event: 'Service event',
  time_off: 'Time off',
};

export function entityLabel(sourceEntityType: string | null): string | null {
  if (!sourceEntityType) return null;
  return ENTITY_LABELS[sourceEntityType] ?? sourceEntityType;
}

/**
 * Drill-down route for an alert's source entity — only where a route exists.
 * Sessions deep-link; other entity kinds have no detail route yet (null).
 */
export function entityHref(
  sourceEntityType: string | null,
  sourceEntityId: string | null,
): string | null {
  if (!sourceEntityType || !sourceEntityId) return null;
  if (sourceEntityType === 'session') return `/sessions/${sourceEntityId}`;
  return null;
}

export function normalizeSeverity(severity: string): AttentionSeverity {
  if (severity === 'critical' || severity === 'warning') return severity;
  return 'info';
}

export function alertToAttentionItem(alert: alerts.Alert, now: number = Date.now()): AttentionItem {
  return {
    id: alert.id,
    severity: normalizeSeverity(alert.severity),
    title: alert.title,
    entity: entityLabel(alert.sourceEntityType) ?? undefined,
    age: ageLabel(alert.createdAt, now),
    // The source record when it has a screen; otherwise the alert itself in
    // Needs Attention — every item opens somewhere.
    href:
      entityHref(alert.sourceEntityType, alert.sourceEntityId) ?? `/attention?alert=${alert.id}`,
  };
}
